'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCard } from '@eliadmin/core/src/components/blog/blog-card';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export const BlogsList = ({ locale }: { locale: string }) => {
  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          order: 'desc',
          orderBy: 'created_at',
          results: 7,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const { t } = useTranslations();

  const blogs = blogsQuery.data?.pages.flatMap(page => page.data) ?? [];

  return (
    <section className={cn('custom-section', 'my-10 sm:my-12 lg:my-14 xl:my-16')}>
      <div
        className={cn(
          'custom-container',
          'grid w-full grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 sm:gap-y-8 md:gap-x-8 lg:grid-cols-3 lg:gap-y-12 xl:gap-y-16',
        )}
      >
        {blogsQuery.error && (
          <p className="col-span-1 text-center text-base text-foreground sm:col-span-2 sm:text-lg lg:text-xl">
            {t('text1472')}
          </p>
        )}
        {/* {blogsQuery.isLoading && <BlogSkeleton />} */}
        {blogsQuery.isSuccess &&
          blogs.map((blog: Blog, i) => (
            <BlogCard blog={blog} key={blog.slug} firstBlog={i === 0} titleAs="h2" />
          ))}
        <div
          className={cn(
            'col-span-1 flex w-full items-center justify-center sm:col-span-2 lg:col-span-3',
            {
              hidden: !blogsQuery.hasNextPage,
            },
          )}
        >
          <Button
            onClick={() => blogsQuery.fetchNextPage()}
            isLoading={blogsQuery.isFetchingNextPage}
            variant={'dark'}
            className="w-full sm:w-fit"
          >
            {t('text588')}
          </Button>
        </div>
      </div>
    </section>
  );
};
